import React, { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { analyzeDocument, analyzeDocuments } from '../config/gemini';
import { analyzeWithOpenAI, uploadFile } from '../config/openai';
import { analyzeDocument as analyzeMistral } from '../config/mistral';
import { testSearchQuery } from '../config/deepResearch';
import './Pages.css';
import DonutProgress from '../components/DonutProgress';
import Navbar from '../components/Navbar';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';

function ESRSReporting() {
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedStandards, setExpandedStandards] = useState({});
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [menuStructure, setMenuStructure] = useState({});
  const [menuLoading, setMenuLoading] = useState(true);
  const [progressMap, setProgressMap] = useState({});
  const [documentContent, setDocumentContent] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState({});
  const [error, setError] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [expandedComments, setExpandedComments] = useState({});

  // Configuration for AI providers - can be modified here to change the provider
  const AI_CONFIG = {
    provider: 'gemini', // Can be 'gemini', 'openai', or 'mistral'
    model: 'gemini-2.0-flash' // Default model
  };

  // Fetch menu structure when component mounts
  useEffect(() => {
    fetchMenuStructure();
  }, []);

  const fetchMenuStructure = async () => {
    try {
      setMenuLoading(true);
      // Fetch all necessary data
      const [sectionsData, standardsData, groupsData, drData] = await Promise.all([
        supabase.from('sections').select('*').order('order_index'),
        supabase.from('standards').select('*').order('order_index'),
        supabase.from('dr_groups').select('*').order('order_index'),
        supabase.from('disclosure_requirements').select('*').order('order_index')
      ]);

      // Check for errors
      if (sectionsData.error) throw sectionsData.error;
      if (standardsData.error) throw standardsData.error;
      if (groupsData.error) throw groupsData.error;
      if (drData.error) throw drData.error;

      // Transform data into menu structure
      const menu = sectionsData.data.reduce((acc, section) => {
        acc[section.id] = {
          title: section.title,
          items: []
        };
        return acc;
      }, {});

      // Add standards to their sections
      standardsData.data.forEach(standard => {
        const menuItem = {
          id: standard.id,
          label: `${standard.code} - ${standard.title}`,
          disclosures: {}
        };

        // Group disclosure requirements by their groups
        const standardDRs = drData.data.filter(dr => dr.standard_id === standard.id);
        groupsData.data.forEach(group => {
          const groupDRs = standardDRs.filter(dr => dr.group_id === group.id);
          if (groupDRs.length > 0) {
            menuItem.disclosures[group.id] = {
              title: group.title,
              items: groupDRs.map(dr => ({
                id: dr.id,
                label: `Disclosure Requirement ${dr.code} - ${dr.title}`
              }))
            };
          }
        });

        menu[standard.section_id].items.push(menuItem);
      });

      setMenuStructure(menu);
    } catch (error) {
      console.error('Error fetching menu structure:', error);
    } finally {
      setMenuLoading(false);
    }
  };

  // Update the existing menuItems reference in the render method
  const menuItems = menuStructure;

  // Fetch questions when a disclosure requirement is selected
  useEffect(() => {
    if (selectedItem) {
      fetchQuestions(selectedItem);
      fetchAnswers(selectedItem);
    }
  }, [selectedItem]);

  const fetchQuestions = async (drId) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('questions')
        .select(`
          *,
          disclosure_requirements (
            id,
            title,
            standards (
              id,
              title,
              sections (
                id,
                title
              )
            )
          )
        `)
        .eq('dr_id', drId)
        .order('order_index');

      if (error) throw error;

      // Transform the data to include standards_title and section_title
      const transformedData = data?.map(question => ({
        ...question,
        standards_title: question.disclosure_requirements?.standards?.title || '',
        section_title: question.disclosure_requirements?.standards?.sections?.title || ''
      })) || [];

      console.log('Transformed questions data:', transformedData);

      setQuestions(transformedData);
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnswers = async (drId) => {
    try {
      const { data, error } = await supabase
        .from('answers')
        .select('*')
        .eq('dr_id', drId);

      if (error) throw error;

      // Transform answers into an object with question_id as keys
      const answersObj = data.reduce((acc, answer) => {
        acc[answer.question_id] = answer.content;
        // Fetch comments for each answer
        fetchComments(answer.id);
        return acc;
      }, {});

      setAnswers(answersObj);
    } catch (error) {
      console.error('Error fetching answers:', error);
    }
  };

  const saveAnswer = async (questionId, content) => {
    console.log('Saving answer:', { questionId, content, selectedItem });

    // Only validate questionId as UUID since dr_id is varchar
    const isValidUUID = (uuid) => /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(uuid);

    if (!isValidUUID(questionId)) {
      console.error('Invalid UUID format for questionId:', questionId);
      return;
    }

    try {
      const timestamp = new Date().toISOString();
      const userId = '00000000-0000-0000-0000-000000000000';

      // First check if an answer exists
      const { data: existingAnswer, error: checkError } = await supabase
        .from('answers')
        .select('id')
        .eq('question_id', questionId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      // Get the dr_id from the questions table first
      const { data: question, error: questionError } = await supabase
        .from('questions')
        .select('dr_id')
        .eq('id', questionId)
        .single();

      if (questionError) {
        console.error('Error fetching question:', questionError);
        throw questionError;
      }

      if (!question) {
        console.error('Question not found');
        return;
      }

      const answerData = {
        question_id: questionId,
        user_id: userId,
        content: content || '',
        status: 'draft',
        created_at: timestamp,
        updated_at: timestamp,
        dr_id: question.dr_id // Use the dr_id from the question
      };

      if (existingAnswer) {
        // Update existing answer
        const { data, error } = await supabase
          .from('answers')
          .update({
            content: answerData.content,
            status: answerData.status,
            updated_at: answerData.updated_at,
            user_id: answerData.user_id
          })
          .eq('id', existingAnswer.id)
          .select();

        if (error) {
          console.error('Error updating answer:', error);
          throw error;
        }
        return data;
      } else {
        // Create new answer
        const { data, error } = await supabase
          .from('answers')
          .insert([answerData])
          .select();

        if (error) {
          console.error('Error inserting answer:', error);
          throw error;
        }
        return data;
      }
    } catch (error) {
      console.error('Error saving answer:', error);
      throw error;
    }
  };

  const handleAnswerChange = async (questionId, content) => {
    console.log('handleAnswerChange called with:', { questionId, content, selectedItem });

    try {
      // Update local state first for immediate UI feedback
      setAnswers(prev => ({
        ...prev,
        [questionId]: content
      }));

      // Save to Supabase
      const savedAnswer = await saveAnswer(questionId, content);
      console.log('Saved answer response:', savedAnswer);

    } catch (error) {
      console.error('Error in handleAnswerChange:', error);
    }
  };

  const toggleStandard = (standardId) => {
    setExpandedStandards(prev => ({
      ...prev,
      [standardId]: !prev[standardId]
    }));
  };

  // Remove the continuous progress calculation effects
  useEffect(() => {
    const initialFetchProgress = async () => {
      if (!menuLoading && Object.keys(menuStructure).length > 0) {
        // Load saved progress from Supabase with a single query
        try {
          const { data: questions } = await supabase
            .from('questions')
            .select('dr_id');
            
          const { data: answers } = await supabase
            .from('answers')
            .select('dr_id, content');
            
          // Group questions by dr_id
          const questionsByDr = questions.reduce((acc, q) => {
            acc[q.dr_id] = (acc[q.dr_id] || 0) + 1;
            return acc;
          }, {});
          
          // Group answered questions by dr_id
          const answeredByDr = answers.reduce((acc, a) => {
            if (a.content && a.content.trim() !== '') {
              acc[a.dr_id] = (acc[a.dr_id] || 0) + 1;
            }
            return acc;
          }, {});
          
          // Calculate progress for each dr_id
          const newProgressMap = {};
          Object.keys(questionsByDr).forEach(drId => {
            const total = questionsByDr[drId] || 0;
            const answered = answeredByDr[drId] || 0;
            if (total > 0) {
              newProgressMap[drId] = Math.round((answered / total) * 100);
            }
          });
          
          setProgressMap(newProgressMap);
        } catch (error) {
          console.error('Error loading initial progress:', error);
        }
      }
    };

    initialFetchProgress();
  }, [menuLoading, menuStructure]);

  const handleSaveProgress = async () => {
    try {
      setIsSaving(true);

      // Save all current answers first
      const savePromises = [];
      for (const [questionId, content] of Object.entries(answers)) {
        savePromises.push(saveAnswer(questionId, content));
      }
      await Promise.all(savePromises);

      // Calculate progress with a single query
      const { data: questions } = await supabase
        .from('questions')
        .select('dr_id');
        
      const { data: answers } = await supabase
        .from('answers')
        .select('dr_id, content');
        
      // Group questions by dr_id
      const questionsByDr = questions.reduce((acc, q) => {
        acc[q.dr_id] = (acc[q.dr_id] || 0) + 1;
        return acc;
      }, {});
      
      // Group answered questions by dr_id
      const answeredByDr = answers.reduce((acc, a) => {
        if (a.content && a.content.trim() !== '') {
          acc[a.dr_id] = (acc[a.dr_id] || 0) + 1;
        }
        return acc;
      }, {});
      
      // Calculate progress for each dr_id
      const newProgressMap = {};
      Object.keys(questionsByDr).forEach(drId => {
        const total = questionsByDr[drId] || 0;
        const answered = answeredByDr[drId] || 0;
        if (total > 0) {
          newProgressMap[drId] = Math.round((answered / total) * 100);
        }
      });
      
      setProgressMap(newProgressMap);
      setIsSaving(false);
      toast.success('Progress saved successfully!');
    } catch (error) {
      console.error('Error saving progress:', error);
      setIsSaving(false);
      toast.error('Error saving progress');
    }
  };

  // Add file icon mapping
  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'txt':
        return '📃';
      default:
        return '📎';
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Create a file object with icon
      const fileObj = {
        file,
        name: file.name,
        icon: getFileIcon(file.name)
      };
      
      // Add to uploaded files
      setUploadedFiles(prev => [...prev, fileObj]);

      // Read file for analysis
      const reader = new FileReader();
      reader.onload = async (e) => {
        const arrayBuffer = e.target.result;
        setDocumentContent(arrayBuffer);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleAnalyzeFiles = async () => {
    if (!uploadedFiles.length || !questions.length) return;

    try {
      setAnalyzing(true);
      setError(null);

      const documentContents = await Promise.all(
        uploadedFiles.map(async (fileObj) => {
          const arrayBuffer = await fileObj.file.arrayBuffer();
          return new Uint8Array(arrayBuffer);
        })
      );

      for (const question of questions) {
        try {
          console.log(`Analyzing question: ${question.question_text}`);
          let answer;
          
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          switch (AI_CONFIG.provider) {
            case 'openai':
              const fileIds = await Promise.all(
                uploadedFiles.map(fileObj => uploadFile(fileObj.file))
              );
              
              const answers = await Promise.all(
                fileIds.map(fileId => 
                  analyzeWithOpenAI(
                    question.question_text,
                    fileId,
                    question.standards_title || '',
                    question.section_title || '',
                    question.paragraph || '',
                    question.related_ar || '',
                    AI_CONFIG.model
                  )
                )
              );
              
              answer = answers.join('\n\nAnalysis from next document:\n\n');
              break;

            case 'mistral':
              answer = await analyzeMistral(
                documentContents,
                question.question_text,
                question.standards_title || '',
                question.section_title || '',
                question.paragraph || '',
                question.related_ar || '',
                AI_CONFIG.model
              );
              break;

            case 'gemini':
            default:
              answer = await analyzeDocuments(
                documentContents,
                question.question_text,
                question.standards_title || '',
                question.section_title || '',
                question.paragraph || '',
                question.related_ar || '',
                AI_CONFIG.model
              );
              break;
          }
          
          if (!answer) {
            console.error('Empty response from AI API');
            throw new Error('Failed to generate answer from the documents');
          }

          setAiAnalysis(prev => ({
            ...prev,
            [question.id]: answer
          }));
          
          await new Promise(resolve => setTimeout(resolve, 500));
          
        } catch (questionError) {
          console.error(`Error analyzing question ${question.id}:`, questionError);
          setError(`Error analyzing question: ${questionError.message}`);
          continue;
        }
      }
    } catch (error) {
      console.error('Error analyzing files:', error);
      setError(`Error analyzing files: ${error.message}`);
    }
    setAnalyzing(false);
  };

  // Add function to copy AI analysis to answer
  const copyAiAnalysisToAnswer = (questionId) => {
    if (aiAnalysis[questionId]) {
      handleAnswerChange(questionId, aiAnalysis[questionId]);
    }
  };

  const handleWebBrowsing = async () => {
    if (!questions.length) return;

    try {
      setAnalyzing(true);
      setError(null);

      // Process questions sequentially
      for (const question of questions) {
        try {
          console.log(`Performing web search for question: ${question.question_text}`);
          const answer = await testSearchQuery(question.question_text);
          
          if (!answer) {
            throw new Error('Failed to generate answer from web search');
          }

          setAiAnalysis(prev => ({
            ...prev,
            [question.id]: answer
          }));
          
          // Wait for state to update before proceeding to next question
          await new Promise(resolve => setTimeout(resolve, 500));
          
        } catch (questionError) {
          console.error(`Error searching question ${question.id}:`, questionError);
          setError(`Error searching question: ${questionError.message}`);
          continue;
        }
      }
    } catch (error) {
      console.error('Error performing web search:', error);
      setError(`Error performing web search: ${error.message}`);
    }
    setAnalyzing(false);
  };

  const handleRemoveFile = (indexToRemove) => {
    setUploadedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleFileClick = (file) => {
    const url = URL.createObjectURL(file);
    window.open(url, '_blank');
    // Clean up the URL object after the window is opened
    URL.revokeObjectURL(url);
  };

  // Add this function near the top of your component
  const formatAIResponse = (text) => {
    if (!text) return '';
    
    // Replace markdown-style formatting with HTML
    return text
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>') // Bold text
      .replace(/\*([^*]+)\*/g, '<em>$1</em>') // Italic text
      .replace(/^- (.+)$/gm, '• $1') // Convert markdown lists to bullet points
      .split('\n').map(line => line.trim()).join('\n'); // Clean up extra spaces
  };

  // Add new function to fetch comments
  const fetchComments = async (answerId) => {
    try {
      const { data, error } = await supabase
        .from('comments')
        .select('*')
        .eq('answer_id', answerId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      setComments(prev => ({
        ...prev,
        [answerId]: data
      }));
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Error loading comments');
    }
  };

  // Add new function to add a comment
  const handleAddComment = async (answerId) => {
    if (!newComment[answerId]?.trim()) return;

    try {
      const timestamp = new Date().toISOString();
      const userId = '00000000-0000-0000-0000-000000000000'; // Replace with actual user ID when auth is implemented

      const { data, error } = await supabase
        .from('comments')
        .insert([{
          answer_id: answerId,
          user_id: userId,
          content: newComment[answerId],
          created_at: timestamp
        }])
        .select();

      if (error) throw error;

      // Update local state
      setComments(prev => ({
        ...prev,
        [answerId]: [...(prev[answerId] || []), data[0]]
      }));

      // Clear the new comment input
      setNewComment(prev => ({
        ...prev,
        [answerId]: ''
      }));

      toast.success('Comment added successfully');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Error adding comment');
    }
  };

  // Add new function to delete a comment
  const handleDeleteComment = async (commentId, answerId) => {
    try {
      const { error } = await supabase
        .from('comments')
        .delete()
        .eq('id', commentId);

      if (error) throw error;

      // Update local state
      setComments(prev => ({
        ...prev,
        [answerId]: prev[answerId].filter(comment => comment.id !== commentId)
      }));

      toast.success('Comment deleted successfully');
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Error deleting comment');
    }
  };

  // Add function to toggle comments visibility
  const toggleComments = (questionId) => {
    setExpandedComments(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const renderQuestions = () => {
    if (loading) {
      return <div className="loading">Loading questions...</div>;
    }

    if (!selectedItem) {
      return (
        <p className="select-message">
          Select a disclosure requirement from the left navigation to begin.
        </p>
      );
    }

    if (questions.length === 0) {
      return (
        <p className="select-message">
          No questions found for this disclosure requirement.
        </p>
      );
    }

    const answeredCount = Object.values(answers).filter(answer => answer && answer.trim() !== '').length;
    const progressPercentage = Math.round((answeredCount / questions.length) * 100);
    const isComplete = progressPercentage === 100;

    return (
      <>
        <div className="progress-section">
          <div className="progress-header">
            <h2>Overall Progress</h2>
            <span className="progress-percentage">
              {`${progressPercentage}% Complete`}
            </span>
          </div>
          <div className="progress-bar-container">
            <div 
              className="progress-bar"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <div className="progress-stats">
            <span className="completed">{`${answeredCount} Completed`}</span>
            <span className="remaining">{`${questions.length - answeredCount} Remaining`}</span>
            <button 
              className="save-progress-btn"
              onClick={handleSaveProgress}
            >
              Save Progress
            </button>
          </div>
        </div>

        <div className="document-analysis">
          <h3>AI-Powered Document Analysis</h3>
          <div className="file-upload-section">
            <div className="upload-buttons">
              <label className="upload-btn">
                <span>Choose Files</span>
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept=".txt,.doc,.docx,.pdf"
                  multiple
                  disabled={analyzing}
                  style={{ display: 'none' }}
                />
              </label>
              <button 
                className="analyze-btn"
                onClick={handleAnalyzeFiles}
                disabled={analyzing || !uploadedFiles.length}
              >
                {analyzing ? 'Analyzing...' : '✨ Analyze Files'}
              </button>
              <button 
                className="deep-research-btn"
                onClick={handleWebBrowsing}
                disabled={analyzing}
              >
                🔗 Web Browsing
              </button>
            </div>
            
            {uploadedFiles.length > 0 && (
              <div className="uploaded-files">
                <h4>Uploaded Files:</h4>
                <ul className="file-list">
                  {uploadedFiles.map((file, index) => (
                    <li key={index} className="file-item">
                      <div 
                        className="file-info"
                        onClick={() => handleFileClick(file.file)}
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="file-icon">{file.icon}</span>
                        <span className="file-name">{file.name}</span>
                      </div>
                      <button 
                        className="file-remove-btn"
                        onClick={() => handleRemoveFile(index)}
                        title="Remove file"
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {analyzing && (
              <div className="analyzing-message">
                Analyzing documents and generating answers...
              </div>
            )}
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
          </div>
        </div>

        <div className="questions-section">
          {questions.map((question) => (
            <div key={question.id} className="question-card">
              <div className="question-header">
                <h3>{question.question_text}</h3>
                <div className="header-actions">
                  <button className="assign-btn">
                    <span className="assign-icon">👤</span>
                    Assign
                  </button>
                  <button 
                    className={`comment-btn ${expandedComments[question.id] ? 'active' : ''}`}
                    onClick={() => toggleComments(question.id)}
                  >
                    <span className="comment-icon">💬</span>
                    Comments ({comments[question.id]?.length || 0})
                  </button>
                </div>
              </div>
              <p className="question-description">
                {`Official Regulation: ${question.paragraph}${question.related_ar ? ` | Related AR: ${question.related_ar}` : ''} | Data Type: ${question.data_type}`}
              </p>
              
              {aiAnalysis[question.id] && (
                <div className="ai-analysis-section">
                  <div className="ai-analysis-header">
                    <span className="ai-icon">✨</span>
                    <h4>AI Analysis Suggestion</h4>
                    <button 
                      className="copy-suggestion-btn"
                      onClick={() => copyAiAnalysisToAnswer(question.id)}
                    >
                      <span>📋</span> Copy to Answer
                    </button>
                  </div>
                  <div 
                    className="ai-analysis-content"
                    dangerouslySetInnerHTML={{ 
                      __html: formatAIResponse(aiAnalysis[question.id])
                        .split('\n')
                        .map(line => line ? `<p>${line}</p>` : '')
                        .join('')
                    }}
                  />
                </div>
              )}
              
              <textarea 
                className="answer-input"
                placeholder="Enter your response here..."
                rows={6}
                value={answers[question.id] || ''}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              />

              {/* Collapsible comments section */}
              {expandedComments[question.id] && (
                <div className="comments-section">
                  <div className="comments-list">
                    {comments[question.id]?.map((comment) => (
                      <div key={comment.id} className="comment">
                        <div className="comment-header">
                          <span className="comment-user">👤 User</span>
                          <span className="comment-date">
                            {new Date(comment.created_at).toLocaleDateString()}
                          </span>
                          <button
                            className="delete-comment-btn"
                            onClick={() => handleDeleteComment(comment.id, question.id)}
                          >
                            ×
                          </button>
                        </div>
                        <p className="comment-content">{comment.content}</p>
                      </div>
                    ))}
                  </div>
                  <div className="add-comment">
                    <textarea
                      placeholder="Add a comment..."
                      value={newComment[question.id] || ''}
                      onChange={(e) => setNewComment(prev => ({
                        ...prev,
                        [question.id]: e.target.value
                      }))}
                      className="comment-input"
                      rows={2}
                    />
                    <button
                      className="add-comment-btn"
                      onClick={() => handleAddComment(question.id)}
                      disabled={!newComment[question.id]?.trim()}
                    >
                      Add Comment
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </>
    );
  };

  // Add this helper function at the top of the component
  const findDisclosureLabel = (menuItems, selectedId) => {
    if (!selectedId || !menuItems) return "Select a disclosure requirement";

    for (const section of Object.values(menuItems)) {
      for (const standard of section.items) {
        if (standard.disclosures) {
          for (const group of Object.values(standard.disclosures)) {
            const disclosure = group.items.find(item => item.id === selectedId);
            if (disclosure) {
              return disclosure.label;
            }
          }
        }
      }
    }
    return "Select a disclosure requirement";
  };

  return (
    <>
      <Navbar selectedDisclosureId={selectedItem} />
      <div className="esrs-container">
        <aside className="esrs-sidebar">
          {menuLoading ? (
            <div className="loading">Loading menu...</div>
          ) : (
            <>
              <div className="sidebar-special-button">
                <Link to="/materiality-analysis" className="dma-button">
                  <span className="dma-icon">📊</span>
                  Double Materiality Analysis
                </Link>
              </div>
              {Object.entries(menuItems).map(([key, section]) => (
                <div key={key} className="sidebar-section">
                  <h3 className="sidebar-title">{section.title}</h3>
                  <ul className="sidebar-list">
                    {section.items.map((item) => (
                      <li key={item.id}>
                        <div 
                          className={`sidebar-item ${selectedItem?.startsWith(item.id) ? 'active' : ''}`}
                          onClick={() => toggleStandard(item.id)}
                        >
                          <div className="sidebar-item-content">
                            <span className="item-label">{item.label}</span>
                            <DonutProgress value={progressMap[item.id] || 0} size={20} />
                          </div>
                        </div>
                        {expandedStandards[item.id] && item.disclosures && (
                          <div className="disclosure-categories">
                            {Object.entries(item.disclosures).map(([catKey, category]) => (
                              <div key={catKey} className="disclosure-category">
                                <div className="category-title">{category.title}</div>
                                <ul className="disclosure-list">
                                  {category.items.map((disclosure) => {
                                    const progress = progressMap[disclosure.id] || 0;
                                    return (
                                      <li 
                                        key={disclosure.id}
                                        className={`disclosure-item ${selectedItem === disclosure.id ? 'active' : ''}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedItem(disclosure.id);
                                        }}
                                      >
                                        <span className="disclosure-label">{disclosure.label}</span>
                                        <DonutProgress value={progress} size={16} />
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            ))}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </>
          )}
        </aside>

        <main className="esrs-main">
          <div className="esrs-header">
            <h1>
              {findDisclosureLabel(menuItems, selectedItem)}
            </h1>
          </div>

          <div className="esrs-content">
            {renderQuestions()}
          </div>
        </main>
      </div>
    </>
  );
}

export default ESRSReporting;